import type { FC, HTMLAttributes, ReactElement } from 'react';
import { useEffect } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useIsLobbyButtonHiddenConnected } from '../../../hooks/useIsLobbyButtonHiddenConnected';
import { useLobbyRedirectFunction } from '../../../hooks/useLobbyRedirectFunction';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { SessionTimer } from '../../compliance/SessionTimer';
import { IconButtonWithTooltip } from '../../controls/iconButton/IconButtonWithTooltip';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { Time } from '../../time/index';
import { Tooltip, TooltipPositions } from '../../tooltip/Tooltip';
import { OverlayLabel } from '../label/OverlayLabel';

import styles from './gameHeader.module.css';

export interface GameHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {}

export const GameHeader: FC<GameHeaderProps> = (rest): ReactElement | null => {
  const audioTrigger = useAudioTrigger();
  const compliance = useConfigSlice.compliance();
  const dispatch = useCoreDispatch();
  const gameId = useGameStateSlice.gameId();
  const aamsData = useConfigSlice.aamsData();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();
  const tableBetLimits = useConfigSlice.tableBetLimits();
  const tableConfig = useConfigSlice.tableConfig();
  const tableName = useConfigSlice.tableName();
  const open = useHeaderSlice.getCoreBetLimitsOpen();
  const handleRedirect = useLobbyRedirectFunction();
  const { betLimitContent } = useGameConfig();
  const hideBetLimit = !betLimitContent;

  const showSessionTimer = useConfigSlice.isSessionTimerVisisble();

  const mods = useModifiers(styles, 'gameHeader', { open }, { withBaseClass: true });
  const showTableLimitsPopup = !!useConfigSlice.jurisdiction()?.showTableLimitsPopup;
  useEffect(() => {
    if (showTableLimitsPopup) {
      dispatch(headerActions.setBetLimitsOpen(true));
    }
  }, [showTableLimitsPopup, dispatch]);

  if (!tableName || !tableBetLimits) {
    return null;
  }

  return (
    <div data-testid="game-header" className={mods} {...rest}>
      <div className={styles.gameHeader__row}>
        {isLobbyButtonHidden ? null : (
          <IconButtonWithTooltip
            data-testid="exit-game-button"
            icon={Icons.Close}
            onClick={(e) => {
              e?.stopPropagation(); // It prevents the click event from propagating to the restoreVolume listener (Ticket: UIC-949)
              handleRedirect();
            }}
            tooltipOptions={{
              position: TooltipPositions.Bottom,
              text: 'EXITGAME',
            }}
          />
        )}
        <div data-testid="game-header-content" className={styles.gameHeader__content}>
          {compliance?.showSessionId ? (
            <div data-testid="game-header-compliance" className={styles.gameHeader__info_compliance}>
              <OverlayLabel small>
                ID-S: {aamsData?.aamsSessionId} ID-P: {aamsData?.aamsTicket}
              </OverlayLabel>
            </div>
          ) : null}
          <button
            data-testid="game-header-title-button"
            className={styles.gameHeader__titleButton}
            onClick={() => {
              dispatch(headerActions.setBetLimitsOpen(!open));
              audioTrigger.play({ name: 'menuClick' });
            }}
            type="button"
            disabled={hideBetLimit}
          >
            <L10nText tk={tableName} />
            {hideBetLimit ? null : (
              <>
                <CurrencyConnected value={tableBetLimits.min} avoidTrailingZero />
                {'-'}
                <CurrencyConnected value={tableBetLimits.max} avoidTrailingZero hideCurrency />
                <Tooltip
                  position={TooltipPositions.Bottom}
                  text={open ? 'CLOSEBETLIMITGSANDPAYOUTS' : 'OPENBETLIMITGSANDPAYOUTS'}
                >
                  <Icon icon={Icons.ChevronDown} className={styles.gameHeader__icon} />
                </Tooltip>
              </>
            )}
          </button>
          <div data-testid="game-header-info" className={styles.gameHeader__info}>
            {tableConfig?.showPPBranding ? <OverlayLabel>PRAGMATIC PLAY &bull;</OverlayLabel> : null}
            <OverlayLabel>
              <Time />
            </OverlayLabel>
            {gameId ? (
              <OverlayLabel>
                <L10nText tk="GAME_NUMBER" />: {gameId}
              </OverlayLabel>
            ) : null}
            <OverlayLabel>{showSessionTimer ? <SessionTimer /> : null}</OverlayLabel>
          </div>
        </div>
      </div>
    </div>
  );
};
